import { useState, useEffect, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Cable, Loader2, RefreshCw } from "lucide-react";

function fmtBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(i ? 1 : 0)} ${u[i]}`;
}

export default function PortBindings({ isAdmin }) {
  const [data, setData] = useState({ items: [], port_range: [8881, 8900], public_host: null, used: 0, capacity: 20 });
  const [pool, setPool] = useState([]);
  const [selected, setSelected] = useState("");
  const [creating, setCreating] = useState(false);

  const publicHost = data.public_host || window.location.hostname;

  const load = useCallback(async () => {
    try {
      const [b, p] = await Promise.all([api.get("/bindings"), api.get("/pool")]);
      setData(b.data);
      setPool(p.data || []);
    } catch (e) {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const boundIds = new Set(data.items.map((x) => x.pool_id));
  const available = pool.filter((p) => !boundIds.has(p.id));

  const create = async () => {
    if (!selected) {
      toast.error("Select an IP first");
      return;
    }
    setCreating(true);
    try {
      const { data: b } = await api.post("/bindings", { pool_id: selected });
      toast.success(`Port ${b.port} opened for ${b.ip}`);
      setSelected("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCreating(false);
    }
  };

  const unbind = async (id, port) => {
    try {
      await api.delete(`/bindings/${id}`);
      toast.success(`Port ${port} closed`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const copy = (text, port) => {
    navigator.clipboard?.writeText(text);
    toast.success(`Port ${port} endpoint copied`);
  };

  return (
    <div className="card-panel overflow-hidden" data-testid="bindings-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-line bg-accent/30">
        <div className="flex items-center gap-2">
          <Cable size={16} className="text-neon" />
          <div>
            <h3 className="font-heading text-base tracking-tight leading-none">Port Forwarding · SOCKS5</h3>
            <span className="overline">
              each port = 1 fixed IP · {data.used}/{data.capacity} ports used · range {data.port_range[0]}-{data.port_range[1]}
            </span>
          </div>
        </div>
        <button onClick={load} className="text-dim hover:text-neon transition-colors" title="Refresh" data-testid="bindings-refresh">
          <RefreshCw size={15} />
        </button>
      </div>

      {isAdmin && (
        <div className="flex flex-wrap items-center gap-2 p-4 border-b border-line">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="h-9 min-w-[280px] flex-1 rounded-md border border-line bg-black px-3 font-mono text-xs text-foreground focus:outline-none focus:border-neon"
            data-testid="bind-ip-select"
          >
            <option value="">— select an IP from pool to open a port —</option>
            {available.map((p) => (
              <option key={p.id} value={p.id}>
                {p.ip} · {p.country} {p.state_code || p.state || ""} {p.city || ""} · {p.isp || "?"}
              </option>
            ))}
          </select>
          <Button
            onClick={create}
            disabled={creating || !selected}
            size="sm"
            className="h-9 rounded-md bg-neon text-white hover:bg-neon/90 font-mono text-xs gap-1.5"
            data-testid="bind-create-btn"
          >
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Open Port
          </Button>
        </div>
      )}

      <div className="overflow-x-auto">
        {data.items.length === 0 ? (
          <p className="p-6 text-center text-sm text-dim font-mono">
            No active SOCKS5 ports yet. {isAdmin ? "Select an IP above then click Open Port." : "Contact the admin to open a port."}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                <th className="px-4 py-2 overline font-normal">Endpoint SOCKS5</th>
                <th className="px-4 py-2 overline font-normal">Exit IP</th>
                <th className="px-4 py-2 overline font-normal">Location</th>
                <th className="px-4 py-2 overline font-normal">Traffic</th>
                <th className="px-4 py-2 overline font-normal">Connections</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((b) => {
                const endpoint = `${publicHost}:${b.port}`;
                return (
                  <tr key={b.id} className="border-b border-line/60 hover:bg-neon/5" data-testid={`binding-row-${b.port}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-sm text-neon font-semibold" data-testid={`binding-endpoint-${b.port}`}>
                          {endpoint}
                        </code>
                        <button
                          onClick={() => copy(endpoint, b.port)}
                          className="text-dim hover:text-neon transition-colors"
                          title="Copy endpoint"
                          data-testid={`binding-copy-${b.port}`}
                        >
                          <Copy size={13} />
                        </button>
                      </div>
                      <span className="font-mono text-[10px] text-dim">socks5 · no auth · port {b.port}</span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{b.exit_ip || "—"}</td>
                    <td className="px-4 py-2.5 text-xs">
                      {b.country} {b.state || ""} {b.city ? `· ${b.city}` : ""}
                      <div className="text-dim text-[11px]">{b.isp}</div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-dim">
                      ↑{fmtBytes(b.bytes_up)} ↓{fmtBytes(b.bytes_down)}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">
                      {b.active}<span className="text-dim"> / {b.connections}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {isAdmin && (
                        <button
                          onClick={() => unbind(b.id, b.port)}
                          className="text-dim hover:text-signal transition-colors"
                          title="Close port"
                          data-testid={`binding-unbind-${b.port}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="px-4 py-2.5 border-t border-line bg-accent/20">
        <span className="font-mono text-[10px] text-dim">
          Usage: set your client to <b>SOCKS5</b> host <b>{publicHost}</b> port <b>{data.port_range[0]}…{data.port_range[1]}</b> (no username/password).
          Example: <code>curl -x socks5h://{publicHost}:{data.items[0]?.port || data.port_range[0]} https://api.ipify.org</code>
        </span>
      </div>
    </div>
  );
}
